import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import './Home.css';

const getSavedValue = (...keys) => {
  for (const key of keys) {
    const value = localStorage.getItem(key);

    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }

  return '';
};

const safeJsonParse = (value) => {
  try {
    if (!value) return null;
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;

    const parts = token.split('.');
    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);

    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const normalizeVendorType = (value) => {
  const text = String(value || '').trim().toLowerCase();

  if (text === 'fastfood' || text === 'food' || text === 'fast_food') return 'fastfood';
  if (text === 'cosmetic' || text === 'cosmetics') return 'cosmetic';
  if (text === 'garment' || text === 'garments') return 'garments';
  if (text === 'grocery') return 'grocery';

  return text || '';
};

const normalizeGroceryMode = (value) => {
  const text = String(value || '').trim().toLowerCase();

  if (text === 'wholesale' || text === 'wholsale') return 'wholesale';
  if (text === 'retail') return 'retail';

  return '';
};

const getDisplayVendorTitle = (vendorType, groceryMode) => {
  if (vendorType === 'grocery') {
    if (groceryMode === 'wholesale') return 'Grocery Wholesale';
    if (groceryMode === 'retail') return 'Grocery Retail';
    return 'Grocery Store';
  }

  if (vendorType === 'cosmetic') return 'Cosmetic Store';
  if (vendorType === 'garments') return 'Garments Store';
  if (vendorType === 'fastfood') return 'Fast Food';

  return 'Vendor Panel';
};

const commonVendorLinks = [
  {
    label: 'Qrpage',
    shortLabel: 'QR',
    path: '/qr-code-display',
    icon: '▦',
  },
  {
    label: 'OrdersReceived',
    shortLabel: 'Orders',
    path: '/ordersReceived',
    icon: '🛍️',
  },
  {
    label: 'Contact Number',
    shortLabel: 'Contact',
    path: '/contact',
    icon: '☎',
  },
  {
    label: 'Records',
    shortLabel: 'Records',
    path: '/vendors',
    icon: '▣',
  },
  {
    label: 'Subscription & Billing',
    shortLabel: 'Billing',
    path: '/subscription-billing',
    icon: '🧾',
  },
  {
    label: 'VendorPaymentCenter',
    shortLabel: 'Payment',
    path: '/VendorPaymentCenter',
    icon: '💳',
  },
];
const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState(false);
  const [vendorType, setVendorType] = useState('');
  const [groceryMode, setGroceryMode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [shopName, setShopName] = useState('');

  const closeMenu = () => {
    setIsActive(false);
    document.body.classList.remove('vakra-menu-open');
  };

  const toggleMenu = () => {
    setIsActive((prev) => {
      const next = !prev;

      if (next) {
        document.body.classList.add('vakra-menu-open');
      } else {
        document.body.classList.remove('vakra-menu-open');
      }

      return next;
    });
  };

  const goToPage = (path) => {
    closeMenu();

    setTimeout(() => {
      navigate(path);
    }, 50);
  };

  useEffect(() => {
    const readVendorInfo = () => {
      const token = localStorage.getItem('token') || '';
      const tokenPayload = decodeJwtPayload(token) || {};

      const savedUser =
        safeJsonParse(localStorage.getItem('user')) ||
        safeJsonParse(localStorage.getItem('vendor')) ||
        safeJsonParse(localStorage.getItem('userData')) ||
        safeJsonParse(localStorage.getItem('vendorData')) ||
        {};

      const savedVendorType =
        getSavedValue(
          'vendorType',
          'vendor_type',
          'businessType',
          'business_type'
        ) ||
        tokenPayload.vendorType ||
        tokenPayload.vendor_type ||
        tokenPayload.businessType ||
        tokenPayload.business_type ||
        savedUser.vendorType ||
        savedUser.vendor_type ||
        savedUser.businessType ||
        savedUser.business_type ||
        '';

      const savedGroceryMode =
        getSavedValue(
          'groceryMode',
          'grocery_mode',
          'groceryModeType',
          'grocery_mode_type',
          'mode'
        ) ||
        tokenPayload.groceryMode ||
        tokenPayload.grocery_mode ||
        tokenPayload.mode ||
        savedUser.groceryMode ||
        savedUser.grocery_mode ||
        savedUser.mode ||
        '';

      const savedVendorName =
        getSavedValue(
          'vendorName',
          'vendor_name',
          'name',
          'userName',
          'user_name'
        ) ||
        tokenPayload.name ||
        savedUser.name ||
        '';

      const savedShopName =
        getSavedValue(
          'shopName',
          'shop_name',
          'storeName',
          'store_name'
        ) ||
        tokenPayload.shopName ||
        tokenPayload.shop_name ||
        savedUser.shopName ||
        savedUser.shop_name ||
        '';

      setVendorType(normalizeVendorType(savedVendorType));
      setGroceryMode(normalizeGroceryMode(savedGroceryMode));
      setVendorName(savedVendorName);
      setShopName(savedShopName);
    };

    readVendorInfo();

    window.addEventListener('storage', readVendorInfo);
    window.addEventListener('focus', readVendorInfo);

    return () => {
      window.removeEventListener('storage', readVendorInfo);
      window.removeEventListener('focus', readVendorInfo);
    };
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    const setNavHeight = () => {
      const nav = document.getElementById('app-navbar');
      if (!nav) return;

      const h = Math.ceil(nav.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--nav-h', `${h}px`);
    };

    setNavHeight();

    window.addEventListener('resize', setNavHeight);

    let ro;
    const nav = document.getElementById('app-navbar');

    if (nav && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => setNavHeight());
      ro.observe(nav);
    }

    return () => {
      window.removeEventListener('resize', setNavHeight);
      if (ro) ro.disconnect();
      document.body.classList.remove('vakra-menu-open');
    };
  }, []);

  const navLinks = useMemo(() => {
    if (vendorType === 'grocery') {
      let groceryHome = {
        label: 'Grocery',
        shortLabel: 'Grocery',
        path: '/grocery-retail',
        icon: '🛒',
      };

      if (groceryMode === 'wholesale') {
        groceryHome = {
          label: 'Grocery Wholesale',
          shortLabel: 'Wholesale',
          path: '/grocery-wholesale',
          icon: '🛒',
        };
      }

      if (groceryMode === 'retail') {
        groceryHome = {
          label: 'Grocery Retail',
          shortLabel: 'Retail',
          path: '/grocery-retail',
          icon: '🛒',
        };
      }

      return [groceryHome, ...commonVendorLinks];
    }

    if (vendorType === 'cosmetic') {
      return [
        {
          label: 'Cosmetic',
          shortLabel: 'Cosmetic',
          path: '/cosmetic',
          icon: '💄',
        },
        ...commonVendorLinks,
      ];
    }

    if (vendorType === 'garments') {
      return [
        {
          label: 'Garments',
          shortLabel: 'Garments',
          path: '/garments',
          icon: '👕',
        },
        ...commonVendorLinks,
      ];
    }

    if (vendorType === 'fastfood') {
      return [
        {
          label: 'Fast Food',
          shortLabel: 'Food',
          path: '/Food',
          icon: '🍔',
        },
        ...commonVendorLinks,
      ];
    }

    return commonVendorLinks;
  }, [vendorType, groceryMode]);

  const businessTitle = getDisplayVendorTitle(vendorType, groceryMode);
  const finalShopName = shopName || businessTitle;
  const finalVendorName = vendorName || 'Vendor';

  return (
    <>
      {isActive ? (
        <button
          type="button"
          className="vakra-mobile-menu-backdrop"
          onClick={closeMenu}
          aria-label="Close menu"
        />
      ) : null}

      <nav id="app-navbar" className="navbar vakra-pro-navbar">
        <div className="vakra-nav-brand">
          <button
            type="button"
            className={`hamburger vakra-hamburger ${isActive ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>

          <button
            type="button"
            className="vakra-brand-link vakra-brand-button"
            onClick={() => goToPage('/ordersReceived')}
          >
            <span className="vakra-brand-icon">V</span>

            <span className="vakra-brand-text">
              <strong>{finalShopName}</strong>
              <small>{businessTitle}</small>
            </span>
          </button>
        </div>

        <ul className={`nav-links vakra-nav-links ${isActive ? 'active' : ''}`}>
          <li className="vakra-drawer-title">
            <div className="vakra-drawer-brand">
              <span>V</span>

              <div>
                <strong>{finalShopName}</strong>
                <small>{businessTitle}</small>
              </div>
            </div>
          </li>

          {navLinks.map((item) => {
            const active = location.pathname === item.path;

            return (
              <li key={item.path}>
                <button
                  type="button"
                  className={active ? 'vakra-nav-item active' : 'vakra-nav-item'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToPage(item.path);
                  }}
                >
                  <span className="vakra-nav-icon">{item.icon}</span>
                  <span className="vakra-nav-label">{item.label}</span>
                  <span className="vakra-nav-short-label">{item.shortLabel}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="vakra-nav-right">
          <div className="vakra-user-chip">
            <div className="vakra-user-avatar">
              {String(finalVendorName || 'V').charAt(0).toUpperCase()}
            </div>

            <div className="vakra-user-info">
              <strong>{finalVendorName}</strong>
              <small>
                {vendorType === 'grocery'
                  ? groceryMode || 'grocery'
                  : vendorType || 'vendor'}
              </small>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Dashboard;
